import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Navigation, MapPin, CheckCircle2, XCircle, PauseCircle, PlayCircle,
  CalendarClock, KeyRound, LocateFixed, Maximize2, Minimize2,
} from "lucide-react";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { STOP_STATUS } from "@/pages/Maintenance";

const REASONS = ["accès impossible", "clé manquante", "client présent", "autre"];
const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function TechRoute({ tour, myPos }) {
  const map = useMap();
  const dirRef = useRef(null);
  useEffect(() => {
    if (!map || !window.google) return;
    const remaining = tour.stops.filter((s) => !["fait", "indisponible", "reporte"].includes(s.status));
    if (!remaining.length || !myPos) {
      if (dirRef.current) { dirRef.current.setMap(null); dirRef.current = null; }
      return;
    }
    if (!dirRef.current) {
      dirRef.current = new window.google.maps.DirectionsRenderer({
        map, suppressMarkers: true,
        polylineOptions: { strokeColor: "#2563EB", strokeWeight: 5, strokeOpacity: 0.85 },
      });
    }
    const pts = remaining.map((s) => ({ location: { lat: s.lat, lng: s.lng }, stopover: true }));
    const dest = pts.pop();
    new window.google.maps.DirectionsService().route(
      { origin: { lat: myPos.lat, lng: myPos.lng }, destination: dest.location, waypoints: pts, travelMode: "DRIVING" },
      (res, st) => { if (st === "OK" && dirRef.current) dirRef.current.setDirections(res); }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, myPos?.lat, myPos?.lng, tour.stops.map((s) => s.status).join(",")]);
  return null;
}

function TechNavMap({ tour, myPos, target }) {
  const [full, setFull] = useState(false);
  if (!GMAPS_KEY) return null;
  const center = myPos || (target && { lat: target.lat, lng: target.lng }) || { lat: 48.8566, lng: 2.3522 };
  return (
    <div className={full
      ? "fixed inset-0 z-[2000] bg-white"
      : "relative h-64 rounded-2xl overflow-hidden border border-slate-200 shadow-sm"}
         data-testid="tech-map">
      <APIProvider apiKey={GMAPS_KEY}>
        <Map key={full ? "full" : "card"} defaultCenter={center} defaultZoom={13} mapId="DEMO_MAP_ID"
             gestureHandling="greedy" disableDefaultUI zoomControl style={{ width: "100%", height: "100%" }}>
          {tour.stops.map((s, i) => (
            <AdvancedMarker key={s.id} position={{ lat: s.lat, lng: s.lng }} data-testid={`tech-map-stop-${s.id}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-lg border-2 border-white ${
                s.status === "fait" ? "bg-emerald-500"
                : s.status === "reporte" || s.status === "indisponible" ? "bg-orange-500"
                : target?.id === s.id ? "bg-blue-600" : "bg-slate-400"}`}>
                {s.status === "fait"
                  ? <CheckCircle2 className="w-4 h-4 text-white" />
                  : s.status === "reporte" || s.status === "indisponible"
                  ? <XCircle className="w-4 h-4 text-white" />
                  : <span className="text-white text-xs font-bold">{i + 1}</span>}
              </div>
            </AdvancedMarker>
          ))}
          {myPos && (
            <AdvancedMarker position={{ lat: myPos.lat, lng: myPos.lng }}>
              <div className="relative" data-testid="tech-my-position">
                <div className="w-5 h-5 rounded-full bg-sky-500 border-2 border-white shadow-lg" />
                <div className="absolute inset-0 rounded-full bg-sky-400 animate-ping opacity-40" />
              </div>
            </AdvancedMarker>
          )}
          <TechRoute tour={tour} myPos={myPos} />
        </Map>
      </APIProvider>
      <button onClick={() => setFull((v) => !v)} data-testid="toggle-map-fullscreen"
              className="absolute top-3 right-3 z-10 bg-white rounded-lg shadow-md border border-slate-200 p-2 hover:bg-slate-50">
        {full ? <Minimize2 className="w-4 h-4 text-slate-700" /> : <Maximize2 className="w-4 h-4 text-slate-700" />}
      </button>
      {full && target && (
        <div className="absolute bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-slate-200 p-3"
             data-testid="fullscreen-mission">
          <div className="text-sm font-bold text-slate-900 truncate">{target.name} — {target.address}</div>
          <div className="text-xs text-slate-600 truncate">{target.problems.map((p) => p.title).join(" · ")}</div>
        </div>
      )}
    </div>
  );
}

export default function TrackTour() {
  const { token } = useParams();
  const [tour, setTour] = useState(null);
  const [error, setError] = useState("");
  const [sharing, setSharing] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [resolveOpen, setResolveOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [myPos, setMyPos] = useState(null);
  const watchRef = useRef(null);
  const lastSent = useRef(0);

  const load = useCallback(() => {
    api.get(`/tours/track/${token}`)
      .then((r) => { setTour(r.data); })
      .catch((e) => setError(e.response?.data?.detail || "Lien invalide"));
  }, [token]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => { clearInterval(t); if (watchRef.current) navigator.geolocation.clearWatch(watchRef.current); };
  }, [load]);

  const startSharing = () => {
    if (!navigator.geolocation) { toast.error("Géolocalisation non supportée sur cet appareil"); return; }
    watchRef.current = navigator.geolocation.watchPosition(
      (p) => {
        setMyPos({ lat: p.coords.latitude, lng: p.coords.longitude });
        const now = Date.now();
        if (now - lastSent.current < 12000) return;
        lastSent.current = now;
        api.post(`/tours/track/${token}/position`, { lat: p.coords.latitude, lng: p.coords.longitude }).catch(() => {});
      },
      () => toast.error("Autorisez la localisation pour le suivi de tournée"),
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
    setSharing(true);
    toast.success("Position partagée avec l'équipe");
  };

  const target = tour?.stops.find((s) => !["fait", "indisponible", "reporte"].includes(s.status)) || null;
  const doneCount = tour?.stops.filter((s) => s.status === "fait").length || 0;

  const answerAvailability = async (available) => {
    try {
      await api.post(`/tours/track/${token}/answer`, { stop_id: target.id, available });
      if (!available) toast.info("Étape marquée indisponible — itinéraire réajusté vers la suivante");
      load();
    } catch { toast.error("Erreur"); }
  };

  const setStatus = async (action, extra = {}) => {
    setBusy(true);
    try {
      await api.post(`/tours/track/${token}/stop/${target.id}/status`, { action, ...extra });
      toast.success(action === "reporte" ? "Intervention reprogrammée" : "Statut mis à jour");
      setReportOpen(false); setReason("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const resolve = async (ok) => {
    setBusy(true);
    try {
      await api.post(`/tours/track/${token}/stop/${target.id}/resolve`, { resolved: ok });
      toast.success(ok ? "Étape terminée — anomalie résolue" : "L'anomalie reste ouverte");
      setResolveOpen(false);
      load();
    } catch { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-sm text-slate-600 max-w-sm">{error}</div>
    </div>
  );
  if (!tour) return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">Chargement…</div>;

  return (
    <div className="min-h-screen bg-slate-50 p-4" data-testid="track-tour-page">
      <div className="max-w-lg mx-auto space-y-4 pb-10">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <h1 className="font-bold font-display text-slate-900">Tournée maintenance — {tour.date}</h1>
            <Badge className="bg-blue-50 text-blue-700 border-blue-200 border">{doneCount}/{tour.stops.length}</Badge>
          </div>
          {!tour.active ? (
            <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-3" data-testid="tour-closed-msg">
              Tournée terminée — merci !
            </p>
          ) : !sharing ? (
            <button onClick={startSharing} data-testid="start-sharing-btn"
                    className="w-full mt-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2">
              <LocateFixed className="w-4 h-4" /> Partager ma position et commencer
            </button>
          ) : (
            <p className="text-xs text-emerald-700 flex items-center gap-1.5 mt-3" data-testid="sharing-indicator">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> Position partagée en direct
            </p>
          )}
        </div>

        {tour.active && <TechNavMap tour={tour} myPos={myPos} target={target} />}

        {tour.active && target?.availability_question && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5 shadow-sm" data-testid="availability-question">
            <div className="text-sm font-bold text-amber-900 mb-1">Vous approchez de « {target.name} »</div>
            <p className="text-sm text-amber-800 mb-3">L'appartement est-il disponible pour l'intervention ?</p>
            <div className="flex gap-2">
              <Button onClick={() => answerAvailability(true)} data-testid="availability-yes"
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">Oui, disponible</Button>
              <Button onClick={() => answerAvailability(false)} data-testid="availability-no"
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white">Non, indisponible</Button>
            </div>
          </div>
        )}

        {tour.active && target && (
          <div className="bg-white rounded-2xl border-2 border-blue-200 p-5 shadow-sm" data-testid="current-stop">
            <div className="flex items-center gap-2 mb-1">
              <MapPin className="w-5 h-5 text-blue-600" />
              <h2 className="font-bold font-display text-slate-900">{target.name}</h2>
              <Badge className={`${STOP_STATUS[target.status]?.cls} border ml-auto`} data-testid="current-stop-status">
                {STOP_STATUS[target.status]?.label}
              </Badge>
            </div>
            <p className="text-sm text-slate-600 ml-7">{target.address}, {target.postal_code} {target.city}</p>
            {target.key_at_office && (
              <p className="text-xs text-sky-700 font-medium ml-7 mt-1 flex items-center gap-1">
                <KeyRound className="w-3.5 h-3.5" /> Clé à récupérer au bureau{target.key_office_box ? ` (boîte n° ${target.key_office_box})` : ""}
              </p>
            )}
            <div className="mt-3 ml-7 space-y-1.5">
              {target.problems.map((p) => (
                <div key={p.id} className="bg-slate-50 rounded-lg p-2.5">
                  <div className="text-sm font-semibold text-slate-800">{p.title}</div>
                  <div className="text-xs text-slate-500">{p.category} · {p.severity}{p.room ? ` · ${p.room}` : ""}</div>
                  {p.description && <div className="text-xs text-slate-600 mt-1">{p.description}</div>}
                </div>
              ))}
              {(() => {
                const photos = target.problems.flatMap((p) => p.photos || []);
                if (!photos.length) return null;
                return (
                  <div className="mt-2" data-testid="track-photos">
                    <div className="text-xs font-semibold text-slate-500 mb-1.5">Photos des anomalies</div>
                    <div className="flex gap-2 flex-wrap">
                      {photos.map((_, i) => (
                        <a key={i} href={`${API}/tours/track/${token}/photo/${target.id}/${i}`} target="_blank" rel="noreferrer">
                          <img src={`${API}/tours/track/${token}/photo/${target.id}/${i}`} alt=""
                               className="h-20 w-20 rounded-lg border border-slate-200 object-cover"
                               data-testid={`track-photo-${i}`} />
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="mt-4 space-y-2">
              {["a_faire", "en_attente"].includes(target.status) && (
                <Button onClick={() => setStatus("en_cours")} disabled={busy} data-testid="start-mission-btn"
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                  <PlayCircle className="w-4 h-4 mr-1.5" /> Je suis sur place — mission en cours
                </Button>
              )}
              {target.status === "en_cours" && (
                <>
                  <Button onClick={() => setResolveOpen(true)} disabled={busy} data-testid="finish-mission-btn"
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Intervention terminée
                  </Button>
                  <Button variant="outline" onClick={() => setStatus("en_attente")} disabled={busy}
                          data-testid="pause-mission-btn" className="w-full">
                    <PauseCircle className="w-4 h-4 mr-1.5" /> Mettre la mission en attente
                  </Button>
                </>
              )}
              <Button variant="outline" onClick={() => setReportOpen(true)} disabled={busy} data-testid="report-mission-btn"
                      className="w-full border-orange-300 text-orange-700 hover:bg-orange-50">
                <CalendarClock className="w-4 h-4 mr-1.5" /> Reprogrammer l'intervention
              </Button>
            </div>
          </div>
        )}

        {tour.active && !target && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center" data-testid="all-done-msg">
            <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
            <div className="text-sm font-semibold text-emerald-800">Toutes les étapes sont traitées !</div>
          </div>
        )}

        <div className="space-y-2">
          {tour.stops.map((s, i) => (
            <div key={s.id} className={`bg-white rounded-xl border px-4 py-3 flex items-center gap-3 ${
              target?.id === s.id ? "border-blue-300" : "border-slate-200"}`} data-testid={`track-stop-${s.id}`}>
              {s.status === "fait" ? <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                : s.status === "indisponible" || s.status === "reporte" ? <XCircle className="w-5 h-5 text-orange-500 flex-shrink-0" />
                : <Navigation className="w-5 h-5 text-slate-400 flex-shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-800 truncate">{i + 1}. {s.name}</div>
                <div className="text-xs text-slate-500 truncate">{s.address}, {s.city}</div>
              </div>
              <Badge className={`${STOP_STATUS[s.status]?.cls} border text-[11px]`}>{STOP_STATUS[s.status]?.label}</Badge>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader><DialogTitle>Reprogrammer l'intervention</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Pourquoi l'intervention ne peut-elle pas avoir lieu maintenant ?</p>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger data-testid="report-reason"><SelectValue placeholder="Choisir un motif…" /></SelectTrigger>
            <SelectContent>
              {REASONS.map((r) => <SelectItem key={r} value={r} data-testid={`reason-${r}`}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReportOpen(false)}>Annuler</Button>
            <Button onClick={() => setStatus("reporte", { reason })} disabled={!reason || busy}
                    data-testid="report-confirm" className="bg-orange-600 hover:bg-orange-700 text-white">
              Reprogrammer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent data-testid="resolve-dialog">
          <DialogHeader><DialogTitle>Le problème a-t-il été complètement résolu ?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Si oui, l'anomalie sera marquée « Résolu » et disparaîtra de l'écran Anomalies.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => resolve(false)} disabled={busy} data-testid="resolve-no">
              Non, pas complètement
            </Button>
            <Button onClick={() => resolve(true)} disabled={busy} data-testid="resolve-yes"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white">Oui, résolu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
